/**
 * Report Builder — 4-column layout.
 *
 *   1) Sections     — list, search, note-count badge, collapse
 *   2) Notes & Snippets — mapped snippets, filters, choices (col-2)
 *   3) Draft Report — docx editor (dominant column)
 *   4) Report QA    — AI quality panel (toggled from the top bar)
 *
 * State + the docx editor ref live in <ReportBuilderEditorProvider/>
 * (mounted in Layout). This page activates the provider and wires the
 * columns together; the editor (col-3) is the only flexible track.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { GripVertical } from "lucide-react";
import { useDispatch } from "react-redux";

import { ReportQAPanel } from "@components/ReportQAPanel";

import { CLIENT_KEYS } from "@constants/clientKeys";

import { useReorderModuleExtracts } from "@hooks/mutations/use-module-extracts";
import { useDownloadResponsePdf } from "@hooks/mutations/use-templates";

import { ASSESSMENT_SECTIONS } from "@pages/ReportStudio/AudioTranscriber/constants/sections";
import { useCaseDetail } from "@pages/ReportStudio/CaseDetail/context";

import { setMessage } from "@redux/notification";

import { getAccessTokenCookie } from "@utils/cookies";
import { getEnv } from "@utils/env";
import { cn } from "@utils/ui";

import { CodeOfConductPanel } from "./components/CodeOfConductPanel";
import { DownloadPdfModal } from "./components/DownloadPdfModal";
import { DraftReportPreviewModal } from "./components/DraftReportPreviewModal";
import { HeaderActions } from "./components/HeaderActions";
import { ModulesPanel } from "./components/ModulesPanel";
import { NotesPanel } from "./components/NotesPanel";
import { ReportSectionEditor } from "./editor/ReportSectionEditor";
import { clampNotesWidth, notesMaxWidth } from "./editor/autosave";
import { useReportBuilderEditor } from "./editor/context";
import { isCodeOfConductSection } from "./editor/utils";

// slug (AssessmentColumnNameEnum) → human label for scribe-note provenance.
// ASSESSMENT_SECTIONS has multiple entries per key (service-type variants); keep
// the first occurrence per key so legacy section titles are used as labels.
const FIELD_LABELS: Record<string, string> = (() => {
  const map = new Map<string, string>();
  ASSESSMENT_SECTIONS.forEach((s) => {
    if (s.key && !map.has(s.key)) map.set(s.key, s.title);
  });
  return Object.fromEntries(map);
})();
const fieldLabel = (slug: string) => FIELD_LABELS[slug] ?? "Scribe";

const ReportBuilderPage = () => {
  const { templateId, id: caseNumber } = useParams({ strict: false }) as {
    templateId: string;
    id: string;
  };
  const { setRightContent } = useCaseDetail();
  const dispatch = useDispatch();

  const {
    activate,
    isLoading,
    sectionRows,
    activeSection,
    showAll,
    setShowAll,
    activeChoices,
    handleSelectSection,
    choiceByModule,
    handleChoiceChange,
    activeNotes,
    allNotes,
    handleSave,
    handleDownload,
    isDownloading,
    draftId,
    caseData,
    template,
  } = useReportBuilderEditor();

  const downloadPdf = useDownloadResponsePdf();
  const reorderNotes = useReorderModuleExtracts();
  const reorderMutateAsync = reorderNotes.mutateAsync;
  const handleReorderNotes = useCallback(
    async (orderedIds: string[]) => {
      if (!caseNumber || !templateId) return;
      try {
        // Rejects on failure so NotesPanel can roll back its optimistic order.
        await reorderMutateAsync({ caseNumber, templateId, orderedIds });
      } catch (error) {
        dispatch(
          setMessage({
            type: "error",
            content:
              error instanceof Error ? error.message : "Failed to save note order.",
          })
        );
        throw error;
      }
    },
    [reorderMutateAsync, caseNumber, templateId, dispatch]
  );
  // `downloadPdf` is a fresh object on every render (React Query observer
  // result), but `.mutateAsync` is bound once in the observer constructor and
  // is stable. Pin the stable ref so `handleConfirmDownloadPdf`'s useCallback
  // doesn't recompute on every render — otherwise the layout-wiring useEffect
  // below re-runs each render and its cleanup (which clears the case-detail
  // header content) trips React's max-update-depth guard.
  const downloadPdfMutateAsync = downloadPdf.mutateAsync;
  const downloadPdfIsPending = downloadPdf.isPending;

  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // Preview modal — render the SAME PDF the Download button produces.
  // The DocxEditor's in-editor render can't paint footers (a
  // documented limitation of the vendored eigenpal editor), so the
  // only way to make Preview match the downloaded artifact is to
  // serve the actual PDF. Trade-off: a few seconds while LibreOffice
  // does the .docx → PDF conversion on each preview open.
  const queryClient = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | undefined>(undefined);
  // Clean up the blob URL after the modal closes. Tracked in a ref
  // (not effect cleanup) because `setPreviewPdfUrl(undefined)` below
  // re-runs this effect immediately, which would clear the timeout
  // before the revoke fires. The ref-based version survives the
  // re-render and only resets if a NEW close lands before the timer.
  const revokeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!previewOpen && previewPdfUrl) {
      const stale = previewPdfUrl;
      if (revokeTimerRef.current) clearTimeout(revokeTimerRef.current);
      // Defer one tick so the iframe finishes unloading before we
      // pull the URL out from under it.
      revokeTimerRef.current = setTimeout(() => {
        URL.revokeObjectURL(stale);
        revokeTimerRef.current = null;
      }, 500);
      setPreviewPdfUrl(undefined);
    }
  }, [previewOpen, previewPdfUrl]);
  const handleOpenPreview = useCallback(async () => {
    // Open the modal IMMEDIATELY with the skeleton, then async-fetch
    // the PDF. The conversion can take several seconds; a synchronous
    // wait would leave the toolbar button looking unresponsive.
    setPreviewPdfUrl(undefined);
    setPreviewOpen(true);
    try {
      // Force-save so the backend re-merges the latest editor state
      // into the saved blob (which download-pdf converts).
      await handleSave();
    } catch {
      // handleSave surfaces its own toast; still attempt the preview
      // in case a previous saved blob exists.
    }
    // handleSave invalidates draft-status; refetch and read fresh
    // data so we have an up-to-date draft response id.
    let draftResponseId: string | undefined;
    if (templateId && caseNumber) {
      const queryKey = [
        CLIENT_KEYS.REPORT_GENERATOR,
        CLIENT_KEYS.TEMPLATES,
        templateId,
        CLIENT_KEYS.CASES,
        CLIENT_KEYS.DRAFT_STATUS,
        caseNumber,
      ];
      try {
        await queryClient.refetchQueries({ queryKey, exact: true });
        const fresh = queryClient.getQueryData<{ draft_id?: string | null }>(
          queryKey
        );
        draftResponseId = fresh?.draft_id ?? draftId ?? undefined;
      } catch {
        draftResponseId = draftId ?? undefined;
      }
    } else {
      draftResponseId = draftId ?? undefined;
    }
    if (!draftResponseId) {
      dispatch(
        setMessage({
          type: "error",
          content: "Save the draft before previewing.",
        })
      );
      setPreviewOpen(false);
      return;
    }
    // Hit the SAME endpoint Download PDF uses -- this is the source
    // of truth for what the user is about to ship. Strip leading
    // slash off the base URL to avoid `//` in the path.
    const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
    const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/responses/${encodeURIComponent(
      draftResponseId
    )}/download-pdf`;
    const token = getAccessTokenCookie();
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        throw new Error(`PDF preview failed: ${res.status}`);
      }
      const blob = await res.blob();
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
      setPreviewPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (err) {
      console.warn("Failed to fetch preview PDF:", err);
      dispatch(
        setMessage({
          type: "error",
          content:
            err instanceof Error ? err.message : "Failed to generate preview PDF.",
        })
      );
      setPreviewOpen(false);
    }
  }, [handleSave, queryClient, templateId, caseNumber, draftId, dispatch]);

  const handleConfirmDownloadPdf = useCallback(
    async (password?: string) => {
      const stem = caseData?.examinee_name?.replace(/\s+/g, "-") ?? caseNumber;
      try {
        // Save first so the current editor .docx is uploaded (draft_docx_url),
        // which is what the PDF endpoint converts. Without this a fresh draft
        // has nothing to export.
        await handleSave();
      } catch {
        // handleSave surfaces its own error toast; still attempt the download
        // in case a previous draft_docx_url exists.
      }
      if (!draftId) {
        const msg = "Save the draft before exporting to PDF.";
        dispatch(setMessage({ type: "error", content: msg }));
        throw new Error(msg);
      }
      try {
        await downloadPdfMutateAsync({
          responseId: draftId,
          fileName: `${stem}-${template?.name ?? "report"}.pdf`,
          password,
        });
      } catch (error) {
        dispatch(
          setMessage({
            type: "error",
            content:
              error instanceof Error
                ? error.message
                : "Failed to download PDF report.",
          })
        );
        throw error;
      }
    },
    [
      handleSave,
      downloadPdfMutateAsync,
      draftId,
      caseData,
      caseNumber,
      template,
      dispatch,
    ]
  );

  const handleDownloadPdf = useCallback(() => {
    setPdfModalOpen(true);
  }, []);

  useEffect(() => {
    if (!caseNumber || !templateId) return;
    activate(caseNumber, templateId);
  }, [activate, caseNumber, templateId]);

  // Column visibility toggles. Editor (col-3) is always present and is the
  // only flexible track — it takes most of the width.
  const [notesHidden, setNotesHidden] = useState(false);
  const [qaHidden, setQaHidden] = useState(true);
  const [sectionsCollapsed, setSectionsCollapsed] = useState(false);

  // MDRS-139: Code of Conduct is a browse-and-copy section. When it's the
  // active section the middle slot renders the CoC panel (jurisdiction
  // dropdown + copy) instead of Notes & Snippets — CoC has no mapped notes and
  // isn't authored inline. Purely derived from the section selection.
  const cocActive = isCodeOfConductSection(activeSection);

  const handleSelectModule = useCallback(
    (key: string) => {
      // Selecting CoC closes QA (the right rail) so the middle slot is the
      // focus; the notes panel is unhidden for every other section.
      if (isCodeOfConductSection(key)) {
        setQaHidden(true);
        handleSelectSection(key);
        return;
      }
      setNotesHidden(false);
      handleSelectSection(key);
    },
    [handleSelectSection]
  );

  // Notes & Snippets is drag-resizable (mirrors Assessment Prep's
  // ResizablePanels): a pointer handle on its left edge writes `notesWidth`,
  // which feeds the connected-card slot AND the sidebar grid track. Min 240px;
  // max is half the main container, measured live per drag (see notesMaxWidth).
  // Sections track is fixed (120 collapsed / 260).
  const NOTES_MIN = 240;
  const [notesWidth, setNotesWidth] = useState(288);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleNotesResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startWidth = notesWidth;
      // Cap the Notes panel at half the main container. Measured once on
      // pointer-down to avoid repeated reflows during the drag.
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 0;
      const max = notesMaxWidth(containerWidth, NOTES_MIN);
      const onMove = (ev: PointerEvent) => {
        // Handle sits on the Notes panel's RIGHT edge → dragging right widens.
        const next = startWidth + (ev.clientX - startX);
        setNotesWidth(clampNotesWidth(next, NOTES_MIN, max));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [notesWidth]
  );

  // Editor is the dominant 1fr track. Sections + Notes share one connected
  // card; editor + QA are separate cards with a gap. Sidebar track width =
  // sections (120 collapsed / 260) + notes (notesWidth if shown). Driven via
  // inline gridTemplateColumns so the notes width can be dynamic (Tailwind
  // can't JIT interpolated arbitrary values).
  // Report QA occupies the single right rail. (Code of Conduct is a section,
  // not a right-rail panel — see cocActive / handleSelectModule.)
  const handleToggleQA = useCallback(() => {
    setQaHidden((v) => !v);
  }, []);

  const sectionsW = sectionsCollapsed ? 120 : 260;
  // The middle slot (notesWidth) is shown when Notes is visible OR when the CoC
  // panel is active (CoC renders there regardless of the notes-hidden toggle).
  const middleSlotShown = cocActive || !notesHidden;
  const sidebarW = sectionsW + (middleSlotShown ? notesWidth : 0);
  const gridTemplateColumns = !qaHidden
    ? `${sidebarW}px 1fr 320px`
    : `${sidebarW}px 1fr`;

  useEffect(() => {
    setRightContent(
      <HeaderActions
        onPreview={handleOpenPreview}
        onToggleQA={handleToggleQA}
        onDownloadDocx={handleDownload}
        onDownloadPdf={handleDownloadPdf}
        isDownloading={isDownloading}
        isDownloadingPdf={downloadPdfIsPending}
        canDownloadPdf={!!draftId}
      />
    );
    return () => {
      setRightContent(null);
    };
  }, [
    setRightContent,
    handleOpenPreview,
    handleToggleQA,
    handleDownload,
    handleDownloadPdf,
    isDownloading,
    downloadPdfIsPending,
    draftId,
  ]);

  return (
    <div
      ref={containerRef}
      className="grid h-full min-h-0 gap-2"
      style={{ gridTemplateColumns }}
    >
      {/* Sections + Notes — one connected card (shared border, internal divider). */}
      <div className="flex min-h-0 overflow-hidden rounded-xl border bg-background-white">
        <div
          className={cn(
            "shrink-0 overflow-hidden transition-[width] duration-200 ease-out",
            sectionsCollapsed ? "w-[120px]" : "w-[256px]"
          )}
        >
          {/* key on collapsed → the swapped panel fades in on toggle */}
          <div
            key={sectionsCollapsed ? "collapsed" : "expanded"}
            className="h-full duration-200 animate-in fade-in"
          >
            <ModulesPanel
              rows={sectionRows}
              activeKey={activeSection}
              isLoading={isLoading}
              onSelect={handleSelectModule}
              collapsed={sectionsCollapsed}
              onToggleCollapse={() => setSectionsCollapsed((v) => !v)}
            />
          </div>
        </div>

        {/* MDRS-139: when Code of Conduct is the active section, the middle
            slot shows the CoC browse-and-copy panel (jurisdiction dropdown +
            copy) instead of Notes & Snippets — CoC carries no mapped notes. */}
        {cocActive ? (
          <div
            className="flex min-h-0 shrink-0 border-l"
            style={{ width: `${notesWidth}px` }}
          >
            <div className="min-w-0 flex-1 p-1.5">
              <CodeOfConductPanel />
            </div>
            {/* Drag handle on the right edge — resize the CoC panel (shares
                notesWidth with Notes & Snippets). */}
            <div
              data-testid="coc-resize-handle"
              onPointerDown={handleNotesResize}
              className="group relative flex w-4 shrink-0 cursor-col-resize select-none items-center justify-center border-l border-border transition-colors hover:border-primary/30 hover:bg-primary/5 active:bg-primary/10"
              title="Drag to resize"
            >
              <GripVertical className="text-muted-foreground/40 size-5 transition-colors group-hover:text-primary group-active:text-primary" />
            </div>
          </div>
        ) : notesHidden ? null : (
          <div
            className="flex min-h-0 shrink-0 border-l"
            style={{ width: `${notesWidth}px` }}
          >
            <div className="min-w-0 flex-1">
              <NotesPanel
                activeNotes={activeNotes}
                allNotes={allNotes}
                activeSection={activeSection}
                showAll={showAll}
                onShowAllChange={setShowAll}
                activeChoices={activeChoices}
                choiceByModule={choiceByModule}
                onChoiceChange={handleChoiceChange}
                fieldLabel={fieldLabel}
                onReorder={handleReorderNotes}
                onCollapse={() => setNotesHidden(true)}
              />
            </div>
            {/* Drag handle on the right edge — resize Notes & Snippets. */}
            <div
              data-testid="notes-resize-handle"
              onPointerDown={handleNotesResize}
              className="group relative flex w-4 shrink-0 cursor-col-resize select-none items-center justify-center border-l border-border transition-colors hover:border-primary/30 hover:bg-primary/5 active:bg-primary/10"
              title="Drag to resize"
            >
              <GripVertical className="text-muted-foreground/40 size-5 transition-colors group-hover:text-primary group-active:text-primary" />
            </div>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border">
        <ReportSectionEditor variant="inline" />
      </div>

      {!qaHidden ? (
        <div className="overflow-hidden rounded-xl border bg-background-white">
          {caseNumber && templateId ? (
            <ReportQAPanel
              templateId={templateId}
              caseNumber={caseNumber}
              onClose={() => setQaHidden(true)}
            />
          ) : null}
        </div>
      ) : null}

      <DraftReportPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfUrl={previewPdfUrl}
        examineeName={caseData?.examinee_name}
        claimNumber={caseData?.claim_number}
        templateName={template?.name}
      />

      <DownloadPdfModal
        open={pdfModalOpen}
        isDownloading={downloadPdfIsPending}
        onOpenChange={setPdfModalOpen}
        onConfirm={handleConfirmDownloadPdf}
      />
    </div>
  );
};

export default ReportBuilderPage;
